import { EventEmitter } from "eventemitter3";
import axios from "axios";
import { nexconsole } from "./proto/client";
import type { IceServer } from "../shared/types";
import {
  createBlackVideoTrack,
  createSilentAudioTrack,
} from "../utils/trackUtils";
import { NetworkPayload } from "./types/NetworkPayload";

export interface NetworkManagerEvents {
  "websocket:connected": () => void;
  "websocket:disconnected": () => void;
  "websocket:error": (error: Error) => void;
  "websocket:connecting": () => void;
  "webrtc:connected": () => void;
  "webrtc:connecting": () => void;
  "webrtc:error": (error: Error) => void;
  "message:received": (messageData: Uint8Array) => void;
  "controller_type_assigned": (isPrimary: boolean) => void;
  "game_started": (url: string) => void;
}

export class NetworkManager extends EventEmitter<NetworkManagerEvents> {
  private ws: WebSocket | null = null;
  private peerConnection: RTCPeerConnection | null = null;
  private dataChannel: RTCDataChannel | null = null;
  private iceServers: IceServer[] | null = null;
  private messageSequence: number = 0;

  constructor(private controllerId: string, private roomId: string) {
    super();
  }

  private generateMessageId(): string {
    return `controller_${this.controllerId}_${Date.now()}_${++this
      .messageSequence}`;
  }

  private createMessageHeader(
    destinationId?: string,
  ) {
    return nexconsole.RoutingHeader.create({
      sourceId: this.controllerId || "",
      sourceType: nexconsole.SourceType.SOURCE_TYPE_CONTROLLER,
      destinationId: destinationId || "",
      destinationType: nexconsole.DestinationType.DESTINATION_TYPE_CONSOLE,
      roomId: this.roomId || "",
      timestampMs: Date.now(),
      messageId: this.generateMessageId(),
    });
  }



  private decodeMessage(buffer: Uint8Array) {
    const decodedMessage = nexconsole.Message.decode(buffer);
    console.log(decodedMessage);
    return decodedMessage;
  }

  async connect(): Promise<void> {
    const wsUrl = `${(import.meta as any).env.VITE_WEBSOCKET_URL
      }?role=controller&roomId=${this.roomId}&clientId=${this.controllerId
      }&clientName=Arpan`; //!

    this.emit("websocket:connecting");
    this.ws = new WebSocket(wsUrl);
    this.ws.binaryType = "arraybuffer";

    this.ws.onopen = () => {
      this.emit("websocket:connected");
      this.attemptWebRTC();
    };

    this.ws.onmessage = (event: MessageEvent) => {
      const buffer = new Uint8Array(event.data);
      const decodedMessage = this.decodeMessage(buffer);
      console.log(decodedMessage);
      this.handleSocketMessage(decodedMessage);
    };

    this.ws.onerror = (error) => {
      this.emit("websocket:error", new Error("WebSocket connection failed"));
    };

    this.ws.onclose = () => {
      this.emit("websocket:disconnected");
    };
  }

  private async attemptWebRTC(): Promise<void> {
    try {
      const relayUrl = (import.meta as any).env.VITE_RELAY_URL;
      const data = await axios.get(`${relayUrl}/ice-servers`);
      this.iceServers = data.data.iceServers;
    } catch (error) {
      console.error("Error loading ICE servers:", error);
    }
    this.setupPeerConnection();
    if (!this.peerConnection) {
      console.log(
        "Missing peerConnection:",
        this.peerConnection
      );
      return;
    }

    try {
      this.emit("webrtc:connecting");
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);
      if (!offer.sdp) return;
      this.sendNetworkMessage({
        offer: {
          controllerId: this.controllerId,
          sdp: offer.sdp,
        }
      });
    } catch (error) {
      console.error("WebRTC connection attempt failed:", error);
      this.emit("webrtc:error", error as Error);
    }
  }

  private setupPeerConnection(): void {
    this.peerConnection = new RTCPeerConnection({
      iceServers: this.iceServers || [],
      iceTransportPolicy: "all",
    });

    this.peerConnection.onicecandidate = (event: RTCPeerConnectionIceEvent) => {
      if (event.candidate) {
        this.sendNetworkMessage({
          iceCandidate: {
            controllerId: this.controllerId,
            candidate: JSON.stringify(event.candidate),
            sdpMid: event.candidate.sdpMid || "",
            sdpMlineIndex: event.candidate.sdpMLineIndex || 0,
            usernameFragment: event.candidate.usernameFragment || "",
          }
        });
      }
    };

    this.dataChannel = this.peerConnection.createDataChannel("data", {
      ordered: false,
      maxRetransmits: 0,
    });

    const audioTrack = createSilentAudioTrack();
    const videoTrack = createBlackVideoTrack();

    const audioStream = new MediaStream([audioTrack]);
    const videoStream = new MediaStream([videoTrack]);

    this.peerConnection.addTrack(audioTrack, audioStream);
    this.peerConnection.addTrack(videoTrack, videoStream);

    this.dataChannel.addEventListener("open", () => {
      console.log(
        `Controller: Data channel opened with console, readyState: ${this.dataChannel?.readyState}`
      );
      this.emit("webrtc:connected");
    });

    this.dataChannel.addEventListener("close", () => {
      console.log("Controller: Data channel closed with console");
    });

    this.dataChannel.addEventListener("error", (error) => {
      console.error("Controller: Data channel error:", error);
    });
  }

  async handleWebRTCAnswer(answer: { sdp: string }): Promise<void> {
    if (this.peerConnection) {
      console.log("Received WebRTC answer");
      await this.peerConnection.setRemoteDescription({
        type: "answer",
        sdp: answer.sdp,
      });
      this.emit("webrtc:connected");
    }
  }

  private handleSocketMessage(decodedMessage: any) {
    if (decodedMessage.serverMessage) {
      this.handleServerMessage(decodedMessage.serverMessage)
    } else if (decodedMessage.peerMessage) {
      this.handlePeerMessage(decodedMessage.peerMessage)
    }
  }

  private handleServerMessage(serverMessage: any) {
    if (serverMessage.controllerTypeAssigned) {
      this.emit('controller_type_assigned', serverMessage.controllerTypeAssigned.isPrimary);
    }
  }

  private handlePeerMessage(peerMessage: any) {
    if (peerMessage.networkPayload) {
      const networkPayload = peerMessage.networkPayload;
      if (networkPayload.answer) {
        this.handleWebRTCAnswer(networkPayload.answer);
      }
    } else if (peerMessage.applicationPayload) {
      const message = peerMessage.applicationPayload;
      if (message.gameStart) {
        console.log("Received game start message:", JSON.stringify(message.gameStart));
        const url = message.gameStart.controllerUrl;
        this.emit("game_started", url);
      }
    }
  }

  sendNetworkMessage(
    networkPayload: NetworkPayload,
    destinationId?: string
  ): void {
    if (!this.ws) {
      console.error(
        "Cannot send message: websocket not connected"
      );
      return;
    }

    try {
      const header = this.createMessageHeader(destinationId);
      const payload = nexconsole.NetworkPayload.create(networkPayload);
      const peerMessage = nexconsole.PeerMessage.create({
        networkPayload: payload,
      });
      const message = nexconsole.Message.create({
        peerMessage: peerMessage
      })

      const relayMessage = nexconsole.RelayMessage.create({
        header,
        payload: message,
      });

      const buffer = nexconsole.RelayMessage.encode(relayMessage).finish();
      this.ws.send(buffer);
    } catch (error) {
      console.error("Failed to send network message:", error);
    }
  }

  sendApplicationMessage(
    applicationPayload: any,
    destinationId?: string
  ): void {
    const transport =
      this.dataChannel && this.dataChannel.readyState === "open"
        ? this.dataChannel
        : this.ws && this.ws.readyState === WebSocket.OPEN
          ? this.ws
          : null;

    if (!transport) {
      console.error("No available transport for sending message");
      return;
    }

    try {
      const payload = nexconsole.ApplicationPayload.create(applicationPayload);

      if (transport instanceof RTCDataChannel) {
        // For WebRTC, send PeerMessage directly
        const peerMessage = nexconsole.PeerMessage.create({
          applicationPayload: payload,
        });

        const message = nexconsole.Message.create({
          peerMessage: peerMessage,
        });

        const buffer = nexconsole.Message.encode(message).finish();
        transport.send(buffer as any);
      } else {
        // For WebSocket, send RelayMessage with header
        const header = this.createMessageHeader(
          destinationId,
        );

        const peerMessage = nexconsole.PeerMessage.create({
          applicationPayload: payload,
        });

        const message = nexconsole.Message.create({
          peerMessage: peerMessage,
        });

        const relayMessage = nexconsole.RelayMessage.create({
          header,
          payload: message,
        });

        const buffer = nexconsole.RelayMessage.encode(relayMessage).finish();
        transport.send(buffer as any);
      }
    } catch (error) {
      console.error("Failed to send application message:", error);
    }
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  isDataChannelOpen(): boolean {
    return this.dataChannel?.readyState === "open" || false;
  }

  sendRoomReadyMessage() {
    this.sendApplicationMessage({
      roomReady: {}
    });
  }
}
